import type { CSSProperties } from 'react';
import { faderPercent, meterPercent, percentToFaderDb } from './audioMath';
import type { MessageKey } from './i18n';
import type { Participant, RoomMode, RoomSnapshot } from './types';

type T = (key: MessageKey) => string;

export function StereoMeter({ left = -60, right = -60, label }: { left?: number; right?: number; label?: string }) {
  return <div className="meter-group" aria-label={label}>
    <div className="meter"><i style={{ '--level': `${meterPercent(left)}%` } as CSSProperties} /><span>L</span></div>
    <div className="meter"><i style={{ '--level': `${meterPercent(right)}%` } as CSSProperties} /><span>R</span></div>
  </div>;
}

export function Fader({ value, label, disabled, onChange, onCommit }: { value: number; label: string; disabled?: boolean; onChange: (value: number) => void; onCommit: (value: number) => void }) {
  return <div className="fader-wrap">
    <input className="fader" aria-label={label} disabled={disabled} type="range" min="0" max="1000"
      value={Math.round(faderPercent(value) * 10)} onChange={event => onChange(percentToFaderDb(Number(event.target.value) / 10))}
      onPointerUp={() => onCommit(value)} onKeyUp={() => onCommit(value)}
      onDoubleClick={event => { event.preventDefault(); onChange(0); onCommit(0); }} />
    <output>{value <= -59.9 ? '−∞' : `${value.toFixed(1)} dB`}</output>
  </div>;
}

export function ParticipantStrip({ participant, disabled, t, onGain, onGainCommit, onMute, onProgram }: {
  participant: Participant; disabled: boolean; t: T; onGain: (value: number) => void; onGainCommit: (value: number) => void; onMute: () => void; onProgram: () => void;
}) {
  return <section className="channel-strip">
    <header><span className={`presence ${participant.connected === false ? 'bad' : ''}`} aria-hidden="true" /><strong>{participant.display_name ?? `${t('artist')} ${participant.participant_id}`}</strong><small>#{participant.participant_id}</small></header>
    <div className="strip-body"><StereoMeter left={participant.left_dbfs} right={participant.right_dbfs} label={t('input')} />
      <Fader value={participant.gain_db} label={t('roomGain')} disabled={disabled} onChange={onGain} onCommit={onGainCommit} /></div>
    <div className="strip-actions">
      <button className={`control-button mute ${participant.muted ? 'active' : ''}`} disabled={disabled} onClick={onMute}>{t('mute')}</button>
      <button className={`control-button program ${participant.included_in_program ? 'active' : ''}`} disabled={disabled} onClick={onProgram}>{t('program')}<span>{t(participant.included_in_program ? 'on' : 'off')}</span></button>
    </div>
  </section>;
}

export function ProgramMaster({ snapshot, t }: { snapshot?: RoomSnapshot; t: T }) {
  const p = snapshot?.program;
  return <section className="program-master">
    <header><span>{t('programMaster')}</span><div className="program-status"><small className={snapshot?.room_server_online ? 'healthy' : 'unavailable'}>{t(snapshot?.room_server_online ? 'healthGood' : 'healthPoor')}</small><b className={p?.limiter_active ? 'limiting' : ''}>{t('limiter')}</b></div></header>
    <div className="program-meters">
      <div><small>{t('preLimiter')}</small><StereoMeter left={p?.pre_left_dbfs} right={p?.pre_right_dbfs} /></div>
      <div><small>{t('postLimiter')}</small><StereoMeter left={p?.post_left_dbfs} right={p?.post_right_dbfs} /></div>
    </div>
    <div className="gain-reduction"><span>{t('gainReduction')}</span><strong>{(p?.gain_reduction_db ?? 0).toFixed(1)} dB</strong></div>
  </section>;
}

export function ModeSelector({ value = 'standby', disabled, t, onChange }: { value?: RoomMode; disabled: boolean; t: T; onChange: (mode: RoomMode) => void }) {
  return <div className="mode-selector" role="group" aria-label={t('mode')}>{(['mc', 'song', 'standby'] as RoomMode[]).map(mode =>
    <button key={mode} className={value === mode ? 'active' : ''} disabled={disabled} onClick={() => onChange(mode)}>{t(mode)}</button>)}</div>;
}
